import { selectProjectUsers } from "@/store/project-selector";
import { useAppSelector } from "@/store/store-hooks";
import {
  FaroText,
  FilterMenuOptionType,
  NO_TRANSLATE_CLASS,
  UserAvatar,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/ielement-types";
import { GenericUserInfo } from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useMemo, type JSX } from "react";
import { ChipFilterWithSearch } from "./chip-filter-with-search";

export const UNASSIGNED_ID = "unassigned";

export type AssigneeFilterProps = {
  /** List of selected assignee IDs. */
  selectedAssigneeIds: GUID[];

  /** Callback executed when the selection changes */
  onSelectedAssigneesChanged(ids: GUID[]): void;
};

/** @returns A chip with a popup that can be used to select a list of users */
export function AssigneeFilter({
  selectedAssigneeIds,
  onSelectedAssigneesChanged,
}: AssigneeFilterProps): JSX.Element {
  const assignees = useAppSelector(selectProjectUsers);

  const options = useMemo<FilterMenuOptionType[]>(
    () => [
      {
        key: UNASSIGNED_ID,
        value: UNASSIGNED_ID,
        label: <FaroText variant="bodyS">Unassigned</FaroText>,
        tooltip: (
          <FaroText variant="bodyS" dark>
            Unassigned
          </FaroText>
        ),
      },
      ...assignees.map((a) => ({
        key: a.id ?? "",
        value: a.name ?? "",
        label: <AssigneeOption user={a} />,
        tooltip: <AssigneeOption user={a} dark />,
      })),
    ],
    [assignees],
  );

  const selectedOptions = useMemo<FilterMenuOptionType[]>(
    () =>
      selectedAssigneeIds
        .map((assigneeId) =>
          options.find((option) => option.key === assigneeId),
        )
        .filter((option) => !!option),
    [selectedAssigneeIds, options],
  );

  return (
    <ChipFilterWithSearch
      label="User"
      selectedOptions={selectedOptions}
      onFilterChange={(selected) => {
        onSelectedAssigneesChanged(selected.map((a) => a.key));
      }}
      options={options}
      headingLabel="Select members"
    />
  );
}

type AssigneeOptionProps = {
  /** The user info */
  user: GenericUserInfo;
  /** True if the option should be rendered using the dark style */
  dark?: boolean;
};

/** @returns a menu item with the user icon and name */
function AssigneeOption({ user, dark }: AssigneeOptionProps): JSX.Element {
  const { name } = user;

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
      <UserAvatar userDisplayInfo={user} size="xs" shouldShowWhiteRim={false} />
      <FaroText className={NO_TRANSLATE_CLASS} variant="bodyS" dark={dark}>
        {name}
      </FaroText>
    </Stack>
  );
}
